import React, { useState, useEffect } from 'react'
import { lumi } from '@/lib/lumi'
import { useACTConversions } from '@/hooks/useACTConversions'
import { socialShare } from '@/lib/socialShare'

// Official ACT to Digital SAT concordance table (College Board 2024)
const actToSat: { [key: number]: { sat: number; percentile: number } } = {
  36: { sat: 1590, percentile: 99 },
  35: { sat: 1540, percentile: 99 },
  34: { sat: 1500, percentile: 98 },
  33: { sat: 1460, percentile: 97 },
  32: { sat: 1430, percentile: 96 },
  31: { sat: 1400, percentile: 95 },
  30: { sat: 1370, percentile: 93 },
  29: { sat: 1340, percentile: 91 },
  28: { sat: 1310, percentile: 88 },
  27: { sat: 1280, percentile: 85 },
  26: { sat: 1240, percentile: 81 },
  25: { sat: 1210, percentile: 77 },
  24: { sat: 1180, percentile: 73 },
  23: { sat: 1140, percentile: 68 },
  22: { sat: 1110, percentile: 63 },
  21: { sat: 1080, percentile: 58 },
  20: { sat: 1040, percentile: 52 },
  19: { sat: 1010, percentile: 46 },
  18: { sat: 980, percentile: 40 },
  17: { sat: 940, percentile: 33 },
  16: { sat: 910, percentile: 27 },
  15: { sat: 880, percentile: 21 },
  14: { sat: 840, percentile: 16 },
  13: { sat: 810, percentile: 12 },
  12: { sat: 780, percentile: 8 },
  11: { sat: 740, percentile: 5 },
  10: { sat: 710, percentile: 3 },
  9: { sat: 680, percentile: 2 },
}

export function ACTConverter() {
  const [actScore, setActScore] = useState<string>('')
  const { saveConversion } = useACTConversions()

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('actConverterData')
    if (saved) {
      try {
        const data = JSON.parse(saved)
        setActScore(data.actScore || '')
      } catch (e) {
        console.error('Failed to load saved data:', e)
      }
    }
  }, [])

  // Save to localStorage whenever value changes
  useEffect(() => {
    localStorage.setItem('actConverterData', JSON.stringify({ actScore }))
  }, [actScore])

  const num = parseInt(actScore)
  const result = num >= 9 && num <= 36 ? actToSat[num] : null

  const handleSaveConversion = async () => {
    const user = lumi.auth.user
    if (!user) {
      alert('Please sign in to save your conversion')
      return
    }

    if (!result) {
      alert('Please enter a valid ACT score (9-36)')
      return
    }

    try {
      await saveConversion({
        actScore: num,
        satEquivalent: result.sat,
        percentile: result.percentile
      })
      alert('Conversion saved successfully!')
    } catch (error) {
      console.error('Failed to save:', error)
      alert('Failed to save conversion')
    }
  }

  const handleEmailReport = async () => {
    const user = lumi.auth.user
    if (!user) {
      alert('Please sign in to email your report')
      return
    }

    if (!result) {
      alert('Please enter a valid ACT score before emailing')
      return
    }

    try {
      await lumi.functions.invoke('sendScoreReport', {
        method: 'POST',
        body: {
          mathRaw: 0,
          readingRaw: 0,
          mathScaled: Math.round(result.sat / 2),
          readingScaled: Math.round(result.sat / 2),
          totalScore: result.sat,
          calculationType: 'act_conversion',
          testNumber: `ACT Score: ${num}`
        }
      })

      alert(`ACT conversion report sent to ${user.email}!`)
    } catch (error: any) {
      console.error('Failed to send email:', error)
      const errorMessage = error?.data?.error || 'Failed to send email report'
      alert(errorMessage)
    }
  }

  const handleDownloadPDF = async () => {
    const user = lumi.auth.user
    if (!user) {
      alert('Please sign in to download your report')
      return
    }

    if (!result) {
      alert('Please enter a valid ACT score before downloading')
      return
    }

    try {
      const result_data = await lumi.functions.invoke('generatePDFReport', {
        method: 'POST',
        body: {
          mathRaw: 0,
          readingRaw: 0,
          mathScaled: Math.round(result.sat / 2),
          readingScaled: Math.round(result.sat / 2),
          totalScore: result.sat,
          calculationType: 'act_conversion',
          testNumber: `ACT Score: ${num}`
        }
      })

      const blob = new Blob([result_data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ACT-to-SAT-Conversion-${result.sat}.pdf`
      a.click()
      URL.revokeObjectURL(url)

      alert('PDF report downloaded successfully!')
    } catch (error: any) {
      console.error('Failed to generate PDF:', error)
      const errorMessage = error?.data?.error || 'Failed to generate PDF report'
      alert(errorMessage)
    }
  }

  const handleCopyLink = async () => {
    const shareData = {
      totalScore: result?.sat || 0,
      calculationType: 'act_conversion',
      actScore: num,
      percentile: result?.percentile
    }
    const success = await socialShare.copyLink(shareData)
    if (success) {
      alert('Link copied to clipboard!')
    } else {
      alert('Failed to copy link')
    }
  }

  return (
    <section className="bg-white rounded-lg shadow-md p-6 md:p-8" aria-labelledby="act-converter-heading">
      <h2 id="act-converter-heading" className="text-3xl font-bold text-gray-900 mb-6">
        ACT to SAT Conversion Calculator
      </h2>
      <p className="text-gray-600 mb-6">
        Convert your ACT score to its SAT equivalent and see your score percentile ranking.
      </p>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Input Section */}
        <div>
          <label htmlFor="act-score" className="block text-sm font-medium text-gray-700 mb-2">
            Your ACT Composite Score
          </label>
          <input
            id="act-score"
            type="number"
            min="1"
            max="36"
            value={actScore}
            onChange={(e) => setActScore(e.target.value)}
            className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="1-36"
            aria-describedby="act-score-hint"
          />
          <p id="act-score-hint" className="text-sm text-gray-500 mt-2">
            Enter your ACT composite score (1-36)
          </p>

          {result && (
            <div className="mt-6 space-y-4">
              <div className="p-6 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
                <div className="flex justify-between items-start mb-4">
                  <div className="text-sm text-gray-600">Equivalent SAT Score</div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleSaveConversion}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors"
                      aria-label="Save conversion to your account"
                    >
                      💾 Save
                    </button>
                    <button
                      onClick={handleEmailReport}
                      className="px-3 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 transition-colors"
                      aria-label="Email conversion report"
                    >
                      📧 Email
                    </button>
                    <button
                      onClick={handleDownloadPDF}
                      className="px-3 py-1 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700 transition-colors"
                      aria-label="Download PDF conversion report"
                    >
                      📄 PDF
                    </button>
                  </div>
                </div>
                
                {/* Social Share Buttons */}
                <div className="flex flex-wrap justify-center gap-2 mb-4 pb-4 border-b border-gray-200">
                  <button
                    onClick={() => socialShare.twitter({
                      totalScore: result.sat,
                      calculationType: 'act_conversion',
                      actScore: num,
                      percentile: result.percentile
                    })}
                    className="px-2 py-1 bg-black text-white rounded hover:bg-gray-800 transition-colors text-xs font-medium"
                    aria-label="Share on Twitter"
                  >
                    𝕏 Share
                  </button>
                  <button
                    onClick={() => socialShare.facebook({
                      totalScore: result.sat,
                      calculationType: 'act_conversion'
                    })}
                    className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs font-medium"
                    aria-label="Share on Facebook"
                  >
                    📘 Facebook
                  </button>
                  <button
                    onClick={() => socialShare.linkedin({
                      totalScore: result.sat,
                      calculationType: 'act_conversion',
                      actScore: num,
                      percentile: result.percentile
                    })}
                    className="px-2 py-1 bg-blue-700 text-white rounded hover:bg-blue-800 transition-colors text-xs font-medium"
                    aria-label="Share on LinkedIn"
                  >
                    💼 LinkedIn
                  </button>
                  <button
                    onClick={() => socialShare.whatsapp({
                      totalScore: result.sat,
                      calculationType: 'act_conversion',
                      actScore: num,
                      percentile: result.percentile
                    })}
                    className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-xs font-medium"
                    aria-label="Share on WhatsApp"
                  >
                    💬 WhatsApp
                  </button>
                  <button
                    onClick={handleCopyLink}
                    className="px-2 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors text-xs font-medium"
                    aria-label="Copy link to clipboard"
                  >
                    🔗 Copy
                  </button>
                </div>
                
                <div className="text-center">
                  <div className="text-5xl font-bold text-blue-600 mb-2">{result.sat}</div>
                  <div className="text-sm text-gray-600">out of 1600</div>
                </div>
              </div>

              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="text-center">
                  <div className="text-sm text-gray-600 mb-1">Score Percentile</div>
                  <div className="text-3xl font-bold text-yellow-700">{result.percentile}%</div>
                  <div className="text-xs text-gray-600 mt-1">
                    Better than {result.percentile}% of test takers
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Conversion Chart */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            ACT to SAT Score Conversion Chart
          </h3>
          <div className="overflow-y-auto max-h-96 border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    ACT
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    SAT
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Percentile
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.from({ length: 28 }, (_, i) => 36 - i).map((act) => {
                  const data = actToSat[act]
                  if (!data) return null
                  return (
                    <tr key={act} className={act === num ? 'bg-blue-50' : ''}>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">{act}</td>
                      <td className="px-4 py-2 text-sm text-blue-600 font-medium">{data.sat}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{data.percentile}%</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  )
}
